/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import * as RadixUiDropdownMenu from "@radix-ui/react-dropdown-menu";
import type { ComponentProps, ReactNode } from "react";
import { Fragment, useEffect, useState } from "react";

import type { ThemeType } from "../../../../../global/emotion";
import {
  numericPixels,
  radius,
  spacing,
  stroke,
  useTheme,
  zIndices,
} from "../../../../../global/emotion";
import { DropdownMenuHeader } from "../DropdownMenuHeader/DropdownMenuHeader.emotion";

const contentStyle: Interpolation<ThemeType> = (theme) => ({
  alignItems: "flex-start",
  backgroundColor: theme.semanticColors.onOverlayBackground,
  borderColor: theme.semanticColors.neutralsLowest,
  borderRadius: radius.l,
  borderStroke: stroke.regular,
  borderStyle: "solid",
  color: theme.semanticColors.primary,
  display: "flex",
  flexDirection: "column",
  gap: spacing.xs,
  maxHeight: "75vh",
  maxWidth: numericPixels[480],
  overflow: "hidden",
  padding: spacing.s,
  zIndex: zIndices.modalContent,
});
const overlayStyle: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.higherContrastMed,
  inset: spacing.none,
  position: "fixed",
  zIndex: zIndices.modalOverlay,
});

const defaultContentProps: ComponentProps<typeof RadixUiDropdownMenu.Content> =
  { sideOffset: 16 };

export type DropdownMenuProps = ComponentProps<
  typeof RadixUiDropdownMenu.Root
> & {
  align?: ComponentProps<typeof RadixUiDropdownMenu.Content>["align"];
  closeOnScroll?: boolean;
  contentProps?: ComponentProps<typeof RadixUiDropdownMenu.Content>;
  contentCSS?: Interpolation;
  header?: {
    text: string;
  } | null;
  overlay?: boolean;
  portalProps?: ComponentProps<typeof RadixUiDropdownMenu.Portal>;
  side?: ComponentProps<typeof RadixUiDropdownMenu.Content>["side"];
  trigger?: ReactNode;
};

export const DropdownMenu = ({
  align = "start",
  children,
  closeOnScroll = false,
  contentProps,
  contentCSS = {},
  header,
  onOpenChange,
  open = false,
  overlay = false,
  portalProps,
  side = "bottom",
  trigger,
  ...props
}: DropdownMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { themeContainerRef } = useTheme();

  const eventListenerHandleScroll = () => setIsOpen(false);

  useEffect(() => {
    if (isOpen !== open) {
      onOpenChange?.(isOpen);
    }
  }, [isOpen]);

  useEffect(() => {
    if (open !== isOpen) {
      setIsOpen(open);
    }
  }, [open]);

  useEffect(() => {
    if (isOpen && closeOnScroll) {
      document.addEventListener("scroll", eventListenerHandleScroll, true);
      return () =>
        document.removeEventListener("scroll", eventListenerHandleScroll);
    }
  }, [isOpen, closeOnScroll]);

  return (
    <RadixUiDropdownMenu.Root open={isOpen} onOpenChange={setIsOpen} {...props}>
      <RadixUiDropdownMenu.Trigger asChild>
        {trigger}
      </RadixUiDropdownMenu.Trigger>
      <RadixUiDropdownMenu.Portal
        container={themeContainerRef.current}
        {...portalProps}
      >
        <Fragment>
          {overlay && <div css={overlayStyle} />}
          <RadixUiDropdownMenu.Content
            css={[contentStyle, contentCSS]}
            {...{ ...defaultContentProps, ...contentProps, align, side }}
          >
            {header && <DropdownMenuHeader>{header.text}</DropdownMenuHeader>}
            {children}
          </RadixUiDropdownMenu.Content>
        </Fragment>
      </RadixUiDropdownMenu.Portal>
    </RadixUiDropdownMenu.Root>
  );
};
